import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Loader2 } from 'lucide-react';
import PortalTooltip from './PortalTooltip';

const TrendChart = ({ entities, trendData = [], isLoading, metricLabel = 'Metric', isPeerMode }) => {
  const [hoveredEntityId, setHoveredEntityId] = useState(null);

  const data = trendData;
  const splitIndex = data.findIndex(d => d.is_forecast === true);
  const cutoffIndex = splitIndex > 0 ? splitIndex - 1 : splitIndex;
  const cutoffPercent = splitIndex !== -1 && data.length > 1 ? (cutoffIndex / (data.length - 1)) * 100 : 100;

  const renderDot = (props, isDashedPart, color, isBase) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    const isForecastPoint = payload.is_forecast === true;
    if (isDashedPart ? !isForecastPoint : isForecastPoint) return null;
    return <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={isBase ? 5 : 3.5} stroke={color} strokeWidth={isBase ? 2.5 : 1.5} fill="#fff" />;
  };

  const renderActiveDot = (props, isDashedPart, color, isBase) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    const isForecastPoint = payload.is_forecast === true;
    if (isDashedPart ? !isForecastPoint : isForecastPoint) return null;
    return <circle key={`${cx}-${cy}-active`} cx={cx} cy={cy} r={isBase ? 7 : 5} stroke="none" fill={color} />;
  };

  const CustomTooltip = ({ active, payload, label, coordinate }) => {
    if (active && payload && payload.length) {
      return (
        <PortalTooltip active={active} payload={payload} coordinate={coordinate}>
          <p className="font-bold text-slate-800 mb-2">{label}</p>
          {payload
            .filter((entry, index, self) => index === self.findIndex(t => t.dataKey === entry.dataKey))
            .sort((a, b) => {
              const valA = (a.value != null && !isNaN(a.value)) ? Number(a.value) : -Infinity;
              const valB = (b.value != null && !isNaN(b.value)) ? Number(b.value) : -Infinity;
              if (valB !== valA) {
                return valB - valA;
              }
              const entityA = entities?.find(e => e.id == a.dataKey?.replace('entity_', ''));
              const entityB = entities?.find(e => e.id == b.dataKey?.replace('entity_', ''));
              return (entityA?.name || '').localeCompare(entityB?.name || '');
            })
            .map((entry, index) => {
              const entityId = entry.dataKey?.replace('entity_', '');
              const entity = entities.find(e => e.id == entityId);
            if (!entity) return null;
            const isHovered = hoveredEntityId == entityId;
            const isAnyHovered = hoveredEntityId !== null;
            const isBase = entity.isBaseEntity;
            
            let itemOpacity = 1;
            if (isPeerMode) {
              itemOpacity = (isBase || isHovered) ? 1 : 0.5;
            } else {
              itemOpacity = isAnyHovered ? (isHovered ? 1 : 0.5) : 1;
            }
            const shouldHighlight = isPeerMode ? (isBase || isHovered) : (isAnyHovered ? isHovered : false);
            
            return (
              <div key={index} className="flex items-center justify-between gap-4 mb-1" style={{ opacity: itemOpacity }}>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entity.color }} />
                  <span style={{ color: entity.color, fontWeight: shouldHighlight ? 'bold' : 'normal' }}>{entity.name}</span>
                </div>
                <span className="font-medium" style={{ color: entity.color, fontWeight: shouldHighlight ? 'bold' : 'normal' }}>
                  {entry.value?.toLocaleString()}
                </span>
              </div>
            );
          })}
        </PortalTooltip>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="relative flex flex-col items-center justify-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">{metricLabel} Over Time</h3>
      </div>
      
      <div className="flex-1 w-full min-h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
            <defs>
              {entities.map(entity => (
                <React.Fragment key={`grad-${entity.id}`}>
                  <linearGradient id={`grad-solid-${entity.id}`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={entity.color} stopOpacity={1} />
                    <stop offset={`${cutoffPercent}%`} stopColor={entity.color} stopOpacity={1} />
                    <stop offset={`${cutoffPercent}%`} stopColor={entity.color} stopOpacity={0} />
                    <stop offset="100%" stopColor={entity.color} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id={`grad-dashed-${entity.id}`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={entity.color} stopOpacity={0} />
                    <stop offset={`${cutoffPercent}%`} stopColor={entity.color} stopOpacity={0} />
                    <stop offset={`${cutoffPercent}%`} stopColor={entity.color} stopOpacity={1} />
                    <stop offset="100%" stopColor={entity.color} stopOpacity={1} />
                  </linearGradient>
                </React.Fragment>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} width={45} />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#e2e8f0', strokeWidth: 1, strokeDasharray: '4 4' }} />
            <Legend 
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} 
              onMouseEnter={(e) => setHoveredEntityId(e.dataKey?.replace('entity_', ''))}
              onMouseLeave={() => setHoveredEntityId(null)}
            />
            
            {entities.map(entity => {
              const isHovered = hoveredEntityId === entity.id;
              const isAnyHovered = hoveredEntityId !== null;
              
              let opacity = 1;
              let strokeW = 2;
              
              if (isPeerMode) {
                if (entity.isBaseEntity) {
                  opacity = 1;
                  strokeW = 3.5;
                } else {
                  opacity = isHovered ? 1 : 0.3;
                  strokeW = isHovered ? 3 : 2;
                }
              } else {
                opacity = 1;
                strokeW = 2;
              }

              return (
              <React.Fragment key={entity.id}>
                {cutoffPercent < 100 ? (
                  <>
                    <Line 
                      type="monotone" 
                      dataKey={`entity_${entity.id}`} 
                      name={entity.name} 
                      stroke={`url(#grad-solid-${entity.id})`} 
                      strokeOpacity={opacity}
                      strokeWidth={strokeW} 
                      dot={(props) => renderDot(props, false, entity.color, entity.isBaseEntity)} 
                      activeDot={(props) => renderActiveDot(props, false, entity.color, entity.isBaseEntity)} 
                      onMouseEnter={() => setHoveredEntityId(entity.id)}
                      onMouseLeave={() => setHoveredEntityId(null)}
                    />
                    <Line 
                      type="monotone" 
                      dataKey={`entity_${entity.id}`} 
                      stroke={`url(#grad-dashed-${entity.id})`} 
                      strokeOpacity={opacity}
                      strokeWidth={strokeW} 
                      strokeDasharray="5 5" 
                      dot={(props) => renderDot(props, true, entity.color, entity.isBaseEntity)} 
                      activeDot={(props) => renderActiveDot(props, true, entity.color, entity.isBaseEntity)} 
                      onMouseEnter={() => setHoveredEntityId(entity.id)}
                      onMouseLeave={() => setHoveredEntityId(null)}
                      legendType="none"
                      tooltipType="none"
                    />
                  </>
                ) : (
                  <Line 
                    type="monotone" 
                    dataKey={`entity_${entity.id}`} 
                    name={entity.name} 
                    stroke={entity.color} 
                    strokeOpacity={opacity}
                    strokeWidth={strokeW} 
                    dot={{ r: entity.isBaseEntity ? 5 : 3.5, strokeWidth: entity.isBaseEntity ? 2.5 : 1.5, fill: "#fff", strokeOpacity: opacity }} 
                    activeDot={{ r: entity.isBaseEntity ? 7 : 5, stroke: "none", fill: entity.color, opacity: opacity }} 
                    onMouseEnter={() => setHoveredEntityId(entity.id)}
                    onMouseLeave={() => setHoveredEntityId(null)}
                  />
                )}
              </React.Fragment>
            )})}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrendChart;
